// NotesPanel - Collaborative notes panel for workspace
// Implements US-4.3: Collaborative Notes Panel

import React, { useState, useEffect } from 'react';
import styles from './NotesPanel.module.css';
import { NoteData, NoteCreatedEvent } from '../types/socket';
import { createNote, getNotes } from '../services/noteService';
import { Socket } from 'socket.io-client';

interface NotesPanelProps {
  workspaceId: string;
  socket: Socket | null;
}

const MAX_NOTE_LENGTH = 500;

/**
 * RENDER collaborative notes panel
 * - Displays list of notes with author info and timestamp
 * - Allows both users and viewers to add notes
 * - Real-time updates via WebSocket
 */
export const NotesPanel: React.FC<NotesPanelProps> = ({ workspaceId, socket }) => {
  const [notes, setNotes] = useState<NoteData[]>([]);
  const [noteContent, setNoteContent] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  /**
   * FETCH notes on component mount
   */
  useEffect(() => {
    const fetchNotes = async () => {
      try {
        setLoading(true);
        const response = await getNotes(workspaceId);

        if (response.success && response.notes) {
          setNotes(response.notes);
        } else {
          setError(response.error || 'Notizen konnten nicht geladen werden');
        }
      } catch (err) {
        console.error('Failed to fetch notes:', err);
        setError('Notizen konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };

    fetchNotes();
  }, [workspaceId]);

  /**
   * LISTEN for real-time note:created events
   */
  useEffect(() => {
    if (!socket) return;

    const handleNoteCreated = (data: NoteCreatedEvent) => {
      // ADD new note to top of list
      setNotes((prevNotes) => [data.note, ...prevNotes]);
    };

    socket.on('note:created', handleNoteCreated);

    return () => {
      socket.off('note:created', handleNoteCreated);
    };
  }, [socket]);

  /**
   * HANDLE note submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedContent = noteContent.trim();

    if (!trimmedContent) {
      return;
    }

    if (trimmedContent.length > MAX_NOTE_LENGTH) {
      setError(`Notiz darf maximal ${MAX_NOTE_LENGTH} Zeichen lang sein`);
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const response = await createNote(workspaceId, trimmedContent);

      if (response.success) {
        // CLEAR input field
        setNoteContent('');
      } else {
        setError(response.error || 'Notiz konnte nicht erstellt werden');
      }
    } catch (err) {
      console.error('Failed to create note:', err);
      setError('Notiz konnte nicht erstellt werden');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * FORMAT timestamp to readable format
   */
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins}m`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `vor ${diffHours}h`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `vor ${diffDays}d`;

    // FALLBACK to date string
    return date.toLocaleDateString('de-DE');
  };

  /**
   * GET role badge class
   */
  const getRoleBadgeClass = (authorType: 'user' | 'viewer'): string => {
    return authorType === 'user' ? (styles.roleBadgeUser || '') : (styles.roleBadgeViewer || '');
  };

  return (
    <div className={styles.notesPanel}>
      {/* HEADER with count */}
      <div className={styles.header}>
        <h3 className={styles.title}>Notizen</h3>
        <span className={styles.count}>{notes.length}</span>
      </div>

      {/* ADD NOTE form */}
      <form onSubmit={handleSubmit} className={styles.noteForm}>
        <div className={styles.inputWrapper}>
          <textarea
            className={styles.noteInput}
            placeholder="Notiz hinzufügen... (Kontext, Entscheidungen, Begründung)"
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            disabled={isSubmitting}
            maxLength={MAX_NOTE_LENGTH}
            rows={3}
          />
          <div className={styles.inputFooter}>
            <span className={styles.charCount}>
              {noteContent.length} / {MAX_NOTE_LENGTH}
            </span>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting || !noteContent.trim()}
            >
              {isSubmitting ? 'Füge hinzu...' : 'Notiz hinzufügen'}
            </button>
          </div>
        </div>
      </form>

      {/* ERROR message */}
      {error && <div className={styles.errorMessage}>{error}</div>}

      {/* NOTES list */}
      <div className={styles.notesList}>
        {loading && <div className={styles.loading}>Lade Notizen...</div>}

        {!loading && notes.length === 0 && (
          <div className={styles.emptyState}>
            <p>Noch keine Notizen</p>
            <p className={styles.emptyHint}>Füge die erste Notiz hinzu, um Kontext und Entscheidungen festzuhalten</p>
          </div>
        )}

        {!loading &&
          notes.map((note) => (
            <div key={note.id} className={styles.noteCard}>
              <div className={styles.noteHeader}>
                <div className={styles.authorInfo}>
                  <span className={styles.authorName}>{note.authorName}</span>
                  <span className={`${styles.roleBadge} ${getRoleBadgeClass(note.authorType)}`}>
                    {note.authorType === 'user' ? 'Ersteller:in' : 'Betrachter:in'}
                  </span>
                </div>
                <span className={styles.timestamp}>{formatTimestamp(note.createdAt)}</span>
              </div>
              <p className={styles.noteContent}>{note.content}</p>
            </div>
          ))}
      </div>
    </div>
  );
};
